import { dealTypeLabelFromSnapshot, sellerDispositionFromSnapshot } from '../../../shared/deal-metadata.js';
import type { DealThread } from '../../../shared/schema.js';
import { freeHouseChipForDeal, isFreeHouseDeal, FREE_HOUSE_REC_META } from './free-house.js';

export type DealListPrimaryContact = {
  id: string;
  name: string;
  partyType: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  role: string | null;
};

export type DealListDeal = DealThread & {
  primaryContact?: DealListPrimaryContact | null;
};

export function snapshotRecord(deal: DealListDeal) {
  const snapshot = deal.parcelSnapshot;
  return snapshot && typeof snapshot === 'object' && !Array.isArray(snapshot)
    ? snapshot as Record<string, unknown>
    : {};
}

export function stringFromSnapshot(snapshot: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = snapshot[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function recordFromUnknown(value: unknown) {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function comparableLabel(value: string | null | undefined) {
  return value
    ?.trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() || null;
}

function matchesReferenceLabel(value: string, references: Array<string | null | undefined>) {
  const comparable = comparableLabel(value);
  if (!comparable) return false;
  return references.some((reference) => comparableLabel(reference) === comparable);
}

function joinNonBlank(parts: Array<string | null | undefined>, separator: string) {
  return parts
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(separator);
}

export function isLaAiSyncedDeal(deal: DealListDeal) {
  const tags = Array.isArray(deal.tags) ? deal.tags : [];
  // Shell-ness dominates: an SPJ job shell is never an LA-AI seller, and a
  // promoted shell can keep sourceApp 'site-prep-jobs' in its snapshot while
  // gaining the la-ai tag - only the shell tag / asset class disqualifies.
  if (deal.assetClass === 'site_prep' || tags.includes('spj-job-shell')) return false;
  if (tags.includes('la-ai')) return true;
  const sourceApp = snapshotRecord(deal).sourceApp;
  return typeof sourceApp === 'string' && sourceApp.trim() === 'land-ally-ai';
}

function dealPrimaryContactName(deal: DealListDeal) {
  const partyName = deal.primaryContact?.name?.trim() || null;
  const rawSnapshotName = stringFromSnapshot(snapshotRecord(deal), ['sellerName', 'seller_name', 'contactName', 'customerName']);
  // GHL contacts are sometimes "named" by their email address; a real party
  // name beats that, and the email only ever serves as a last resort.
  const snapshotName = rawSnapshotName && !rawSnapshotName.includes('@') ? rawSnapshotName : null;
  // LA-AI is canonical for synced deals, and the snapshot is what LA-AI last
  // pushed. Parties are append-only local rows, so a thread that changed hands
  // (the Aug 8 Willis Staggs takeover) can keep a stale person attached; the
  // displayed name must follow the sync, with the party only as a fallback.
  if (isLaAiSyncedDeal(deal)) return snapshotName || partyName || rawSnapshotName;
  return partyName || snapshotName || rawSnapshotName;
}

function dealAddressLabel(deal: DealListDeal) {
  const snapshot = snapshotRecord(deal);
  const identity = recordFromUnknown(snapshot.identity);
  const leadData = recordFromUnknown(snapshot.leadData);
  const leadIdentity = recordFromUnknown(leadData.identity);
  const laAi = recordFromUnknown(snapshot.laAi);
  const laAiIdentity = recordFromUnknown(laAi.identity);
  const aiAnalysis = recordFromUnknown(snapshot.aiAnalysis);
  const aiAnalysisIdentity = recordFromUnknown(aiAnalysis.identity);
  const records = [snapshot, identity, leadData, leadIdentity, laAi, laAiIdentity, aiAnalysis, aiAnalysisIdentity];
  const referenceLabels = [
    deal.primaryContact?.name,
    stringFromSnapshot(snapshot, ['sellerName', 'seller_name', 'contactName', 'customerName']),
    stringFromSnapshot(laAi, ['sellerName', 'seller_name', 'contactName', 'customerName']),
  ];
  const firstString = (keys: string[]) => {
    for (const record of records) {
      const value = stringFromSnapshot(record, keys);
      if (value && matchesReferenceLabel(value, referenceLabels)) continue;
      if (value) return value;
    }
    return null;
  };

  const direct = firstString([
    'fullAddress',
    'propertyFullAddress',
    'fullPropertyAddress',
    'parcelFullAddress',
    'address1',
    'parcelAddress',
    'parcel_address',
    'situsFullAddress',
    'situsAddress',
    'situs_address',
    'mailingAddress',
    'mailing_address',
    'propertyAddress',
    'property_address',
    'siteAddress',
    'site_address',
    'privateSiteAddress',
    'publicAddress',
    'address',
    'dealLocation',
  ]);
  const city = firstString(['propertyCity', 'property_city', 'city']);
  const state = firstString(['propertyState', 'property_state', 'state']);
  const county = firstString(['propertyCounty', 'property_county', 'county']);
  const location = firstString(['publicLocationLabel', 'location']);

  if (direct) {
    const suffix = joinNonBlank([city, state], ', ');
    if (suffix && !direct.toLowerCase().includes(suffix.toLowerCase())) {
      return joinNonBlank([direct, suffix], ', ');
    }
    return direct;
  }

  return joinNonBlank([location || county, state], ', ') || null;
}

function stringFromUnknown(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

const MISSING_LABEL_VALUES = new Set(['', '0', 'unknown', 'n/a', 'na', 'none', 'null', 'undefined', 'tbd', '-']);
const GENERIC_SOURCE_LABELS = new Set(['land ally ai', 'land-ally-ai', 'la ai', 'la-ai', 'poe', 'personal outreach engine']);
const HIDDEN_OPERATOR_DEAL_TAGS = new Set([
  'ai',
  'la-ai',
  'spj',
  'spj-job-shell',
  'unmatched_email',
]);

function cleanHumanLabel(value: unknown) {
  const text = stringFromUnknown(value);
  if (!text) return null;
  const normalized = text.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (MISSING_LABEL_VALUES.has(normalized)) return null;
  return text;
}

function sourceLabelFromApp(value: unknown) {
  const text = cleanHumanLabel(value);
  if (!text) return null;
  const normalized = text.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (normalized === 'site prep jobs') return 'Site Prep Jobs';
  if (normalized === 'land ally ai') return 'Land Ally AI';
  return text;
}

function explicitLeadSourceFromRecord(record: Record<string, unknown>) {
  const candidates = [
    record.leadSource,
    record.lead_source,
    record.originalLeadSource,
    record.original_lead_source,
    record.actualLeadSource,
    record.actual_lead_source,
    record.ghlSource,
    record.ghl_source,
    record.marketingSource,
    record.marketing_source,
    record.campaignSource,
    record.campaign_source,
    record.utmSource,
    record.utm_source,
    record.dealSource,
    record.deal_source,
    record.source,
  ];

  for (const candidate of candidates) {
    const label = cleanHumanLabel(candidate);
    const normalized = label?.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (label && !GENERIC_SOURCE_LABELS.has(normalized ?? '')) return label;
  }
  return null;
}

function dealSourceTagValue(tags: string[] | null | undefined) {
  if (!Array.isArray(tags)) return null;
  for (const tag of tags) {
    if (typeof tag !== 'string') continue;
    const lower = tag.toLowerCase();
    if (!lower.startsWith('source:')) continue;
    const label = cleanHumanLabel(tag.slice('source:'.length).replace(/[-_]+/g, ' '));
    if (label) return label;
  }
  return null;
}

export function formatDealTagLabel(tag: string) {
  return tag.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

export function isOperatorDealTag(tag: unknown): tag is string {
  if (typeof tag !== 'string') return false;
  const trimmed = tag.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (HIDDEN_OPERATOR_DEAL_TAGS.has(lower)) return false;
  if (lower.startsWith('source:')) return false;
  if (lower.startsWith('spj-job:')) return false;
  if (lower.startsWith('free-house:')) return false;
  return true;
}

export function visibleDealTags(deal: DealListDeal, limit = 3) {
  const tags = Array.isArray(deal.tags) ? deal.tags : [];
  return Array.from(new Set(tags.filter(isOperatorDealTag))).slice(0, limit);
}

function stringArrayFromUnknown(value: unknown) {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0) : [];
}

function numberFromLooseValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function firstPositiveCount(records: Array<Record<string, unknown>>, keys: string[]) {
  for (const record of records) {
    for (const key of keys) {
      const value = record[key];
      if (Array.isArray(value) && value.length > 0) return value.length;
      const count = numberFromLooseValue(value);
      if (count && count > 0) return count;
    }
  }
  return 0;
}

const OPEN_SPJ_JOB_STATUSES = new Set([
  'bid_review',
  'bidding',
  'collecting_bids',
  'needs_award',
  'open',
  'pending_award',
  'posted',
]);

const AWARDED_SPJ_JOB_STATUSES = new Set([
  'accepted',
  'assigned',
  'awarded',
  'cancelled',
  'canceled',
  'closed',
  'complete',
  'completed',
  'in_progress',
  'scheduled',
]);

function normalizedJobStatus(value: unknown) {
  return typeof value === 'string'
    ? value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    : '';
}

export function dealSitePrepJobSummary(deal: DealListDeal) {
  const snapshot = snapshotRecord(deal);
  const nestedJob = recordFromUnknown(snapshot.sitePrepJob);
  const tags = Array.isArray(deal.tags) ? deal.tags : [];
  const tagJobIds = tags
    .filter((tag) => typeof tag === 'string' && tag.startsWith('spj-job:'))
    .map((tag) => tag.slice('spj-job:'.length))
    .filter(Boolean);
  const directJobId = stringFromUnknown(snapshot.spjJobId) || stringFromUnknown(snapshot.jobId);
  const nestedJobId = stringFromUnknown(nestedJob.spjJobId) || stringFromUnknown(nestedJob.jobId);
  const jobIds = Array.from(new Set([directJobId, nestedJobId, ...tagJobIds].filter((id): id is string => Boolean(id))));
  const hasNestedJob = Object.keys(nestedJob).length > 0;
  if (jobIds.length === 0 && !hasNestedJob) return null;

  const records = [snapshot, nestedJob];
  const services = Array.from(new Set(records.flatMap((record) => stringArrayFromUnknown(record.services))));
  const explicitOpenCount = firstPositiveCount(records, [
    'openServiceCount',
    'openServicesCount',
    'servicesOpen',
    'unawardedServiceCount',
    'unawardedServicesCount',
    'pendingAwardServiceCount',
    'pendingAwardServicesCount',
    'openServices',
    'unawardedServices',
    'pendingAwardServices',
  ]);
  const status = normalizedJobStatus(nestedJob.status ?? snapshot.status);
  const statusLooksOpen = OPEN_SPJ_JOB_STATUSES.has(status);
  const statusLooksAwarded = AWARDED_SPJ_JOB_STATUSES.has(status);
  const openServicesCount = explicitOpenCount || (statusLooksOpen && services.length > 0 ? services.length : 0);
  const needsAward = openServicesCount > 0 || (statusLooksOpen && !statusLooksAwarded);
  const jobCount = Math.max(jobIds.length, hasNestedJob ? 1 : 0);

  return {
    jobCount,
    servicesCount: services.length,
    openServicesCount,
    needsAward,
    label: needsAward ? (openServicesCount > 0 ? `${openServicesCount} open` : 'Needs award') : (jobCount === 1 ? 'Job linked' : `${jobCount} jobs`),
  };
}

export function dealLeadSourceLabel(deal: DealListDeal) {
  const snapshot = snapshotRecord(deal);
  const leadData = recordFromUnknown(snapshot.leadData);
  const kpiVariables = recordFromUnknown(leadData.kpiVariables);
  const laAi = recordFromUnknown(snapshot.laAi);
  const aiAnalysis = recordFromUnknown(snapshot.aiAnalysis);
  const records = [snapshot, leadData, kpiVariables, laAi, aiAnalysis];

  for (const record of records) {
    const source = explicitLeadSourceFromRecord(record);
    if (source) return source;
  }

  const tagSource = dealSourceTagValue(deal.tags);
  if (tagSource) return tagSource;

  if (snapshot.spjJobId || snapshot.jobId) return 'Site Prep Jobs';
  const sourceApp = sourceLabelFromApp(snapshot.sourceApp);
  if (sourceApp && sourceApp !== 'Land Ally AI') return sourceApp;
  if (deal.laAiDealId) return 'Unsynced';
  if ((deal.tags ?? []).includes('unmatched_email') || (deal.tags ?? []).includes('needs_review')) return 'Inbox';
  return 'Manual';
}

export function dealLeadSourceFilterValue(deal: DealListDeal) {
  const snapshot = snapshotRecord(deal);
  const leadData = recordFromUnknown(snapshot.leadData);
  const kpiVariables = recordFromUnknown(leadData.kpiVariables);
  const laAi = recordFromUnknown(snapshot.laAi);
  const aiAnalysis = recordFromUnknown(snapshot.aiAnalysis);
  const records = [snapshot, leadData, kpiVariables, laAi, aiAnalysis];

  for (const record of records) {
    const source = explicitLeadSourceFromRecord(record);
    if (source) return source;
  }

  if (!Array.isArray(deal.tags)) return null;
  return deal.tags.find((tag) => typeof tag === 'string' && tag.toLowerCase().startsWith('source:')) ?? null;
}

function numberFromUnknown(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// LA-AI's deal-analysis generator (server/routes/_helpers.ts) stores these
// fallback strings when ITS OWN response parsing fails; they sync into POE's
// parcelSnapshot.aiAnalysis. Treat them as "no summary" so the AI Summary
// popover never shows the raw error to the operator (the deals list only
// renders the popover when a summary is present). The durable fix lives
// upstream in LA-AI (handed to Tasmeer); this is the display-side guard.
const AI_ANALYSIS_ERROR_SENTINELS = new Set([
  'unable to parse ai analysis response.',
  'error generating analysis.',
]);

function withoutAnalysisErrorSentinel(value: string | null) {
  if (value && AI_ANALYSIS_ERROR_SENTINELS.has(value.trim().toLowerCase())) return null;
  return value;
}

export function dealAiSummary(deal: DealListDeal) {
  const snapshot = snapshotRecord(deal);
  const aiAnalysis = snapshot.aiAnalysis && typeof snapshot.aiAnalysis === 'object' && !Array.isArray(snapshot.aiAnalysis)
    ? snapshot.aiAnalysis as Record<string, unknown>
    : {};
  const laAi = snapshot.laAi && typeof snapshot.laAi === 'object' && !Array.isArray(snapshot.laAi)
    ? snapshot.laAi as Record<string, unknown>
    : {};

  return withoutAnalysisErrorSentinel(
    stringFromSnapshot(snapshot, ['aiSummary', 'aiDealSummary', 'dealSummary', 'laAiSummary']) ||
    stringFromUnknown(aiAnalysis.dealSummary) ||
    stringFromUnknown(laAi.aiSummary) ||
    null
  );
}

export function dealAiRecommendedAction(deal: DealListDeal) {
  const snapshot = snapshotRecord(deal);
  const aiAnalysis = snapshot.aiAnalysis && typeof snapshot.aiAnalysis === 'object' && !Array.isArray(snapshot.aiAnalysis)
    ? snapshot.aiAnalysis as Record<string, unknown>
    : {};
  const laAi = snapshot.laAi && typeof snapshot.laAi === 'object' && !Array.isArray(snapshot.laAi)
    ? snapshot.laAi as Record<string, unknown>
    : {};

  return (
    stringFromSnapshot(snapshot, ['aiRecommendedAction', 'recommendedAction', 'nextBestAction']) ||
    stringFromUnknown(aiAnalysis.recommendedAction) ||
    stringFromUnknown(laAi.recommendedAction) ||
    null
  );
}

export function dealAiPursuitScore(deal: DealListDeal) {
  const snapshot = snapshotRecord(deal);
  const aiAnalysis = snapshot.aiAnalysis && typeof snapshot.aiAnalysis === 'object' && !Array.isArray(snapshot.aiAnalysis)
    ? snapshot.aiAnalysis as Record<string, unknown>
    : {};
  const laAi = snapshot.laAi && typeof snapshot.laAi === 'object' && !Array.isArray(snapshot.laAi)
    ? snapshot.laAi as Record<string, unknown>
    : {};

  return (
    numberFromUnknown(snapshot.pursuitScore) ??
    numberFromUnknown(snapshot.aiPursuitScore) ??
    numberFromUnknown(aiAnalysis.pursuitScore) ??
    numberFromUnknown(laAi.pursuitScore)
  );
}

export function dealDisplayTitle(deal: DealListDeal) {
  const snapshot = snapshotRecord(deal);
  const contactName = dealPrimaryContactName(deal);
  const addressLabel = dealAddressLabel(deal);
  if (contactName && addressLabel) return `${contactName} - ${addressLabel}`;
  if (contactName) return contactName;

  return (
    stringFromSnapshot(snapshot, ['dealName', 'sellerDealName', 'laAiDealName']) ||
    deal.title?.trim() ||
    stringFromSnapshot(snapshot, ['jobTitle', 'title']) ||
    'Untitled deal'
  );
}

export function dealCurrentStageLabel(deal: DealListDeal) {
  // FHS (sfh) deals are exempt from seller-disposition views; surface their
  // score bucket (Pursue/Review/Pass) as the lifecycle label instead, and never
  // read a seller disposition even if a synced candidate retained one.
  if (isFreeHouseDeal(deal)) {
    const freeHouse = freeHouseChipForDeal(deal);
    return freeHouse ? FREE_HOUSE_REC_META[freeHouse.recommendation].label : 'Not scored';
  }
  return sellerDispositionFromSnapshot(snapshotRecord(deal)) || 'Current stage missing';
}

export function dealTypeDisplayLabel(deal: DealListDeal) {
  return dealTypeLabelFromSnapshot(snapshotRecord(deal), deal.assetClass);
}
